import { resourceAssignmentsConf } from '../../api/resources';
import isEqual from 'lodash-es/isEqual';
import transform from 'lodash-es/transform';
import isObject from 'lodash-es/isObject';
import orderBy from 'lodash-es/orderBy';
import { formatTime } from '../common/helpers';

export const DEFAULT_ASSIGNMENTS_CONFIGURATION = {
    show_resident_call: true,
    resident_call_name: 'Room',
    resident_color: 2,
    show_staff_call: true,
    staff_call_name: 'Staff',
    staff_color: 1,
    assignments_type: 0,
    assignments_level: 1,
    show_catch_net: true,
    staff_overload: 100,
};

export default {
    data() {
        return {
            assignmentsConf: {},
            callIdLocationCount: {},
        };
    },

    methods: {
        /**
         * Loads assignments configuration from database
         */
        loadAssignmentsConf() {
            this.showLoading(true);
            return resourceAssignmentsConf({
                cancelToken: this.cancelToken ? this.cancelToken.token : undefined,
            })
                .then(response => {
                    this.assignmentsConf = response.data;
                })
                .catch(() => {
                    this.assignmentsConf = { ...DEFAULT_ASSIGNMENTS_CONFIGURATION };
                });
        },

        /**
         * Get all call id's from location
         * @param {*} location
         */
        getCallIdsFromLocation(location) {
            let callIds = [];

            // Direct assignments
            location.dio1.items.forEach(item => {
                if (item.hasOwnProperty('callIds')) {
                    item.callIds.forEach(callId => callIds.push(callId.toString()));
                }
            });
            location.dio2.items.forEach(item => {
                if (item.hasOwnProperty('callIds')) {
                    item.callIds.forEach(callId => callIds.push(callId.toString()));
                }
            });

            location.dia1.items.forEach(item => {
                if (item.hasOwnProperty('callIds')) {
                    item.callIds.forEach(callId => callIds.push(callId.toString()));
                }
            });
            location.dia2.items.forEach(item => {
                if (item.hasOwnProperty('callIds')) {
                    item.callIds.forEach(callId => callIds.push(callId.toString()));
                }
            });

            // Duty assignments (all shifts)
            Object.values(location.duo1).forEach(shift =>
                shift.items.forEach(item => {
                    if (item.hasOwnProperty('callIds')) {
                        item.callIds.forEach(callId => callIds.push(callId.toString()));
                    }
                })
            );
            Object.values(location.duo2).forEach(shift =>
                shift.items.forEach(item => {
                    if (item.hasOwnProperty('callIds')) {
                        item.callIds.forEach(callId => callIds.push(callId.toString()));
                    }
                })
            );

            Object.values(location.dua1).forEach(shift =>
                shift.items.forEach(item => {
                    if (item.hasOwnProperty('callIds')) {
                        item.callIds.forEach(callId => callIds.push(callId.toString()));
                    }
                })
            );
            Object.values(location.dua2).forEach(shift =>
                shift.items.forEach(item => {
                    if (item.hasOwnProperty('callIds')) {
                        item.callIds.forEach(callId => callIds.push(callId.toString()));
                    }
                })
            );
            return callIds;
        },

        /**
         * Check and mark overloaded staff
         * @param {*} staff
         */
        checkStaff(staff) {
            for (let callIdNumber of staff.callIds) {
                let callId = callIdNumber.toString();
                if (this.callIdLocationCount.hasOwnProperty(callId)) {
                    let locations = new Set(this.callIdLocationCount[callId]);
                    if (locations.size > this.assignmentsConf.staff_overload) {
                        return true;
                    }
                }
            }
            return false;
        },

        /**
         * Create list overloaded staff
         * @param {*} locations
         */
        calcOverloadedStaff(locations) {
            locations.forEach(location => {
                let callIds = this.getCallIdsFromLocation(location);
                callIds.forEach(callIdNumber => {
                    let callId = callIdNumber.toString();
                    if (this.callIdLocationCount.hasOwnProperty(callId)) {
                        this.callIdLocationCount[callId].push(location.location_id);
                    } else {
                        this.callIdLocationCount[callId] = [location.location_id];
                    }
                });
            });
        },

        /**
         * Check, if location contains any overloaded call id
         * @param {*} location
         */
        hasOverloaded(location) {
            let callIds = this.getCallIdsFromLocation(location);
            for (let callId of callIds) {
                if (this.callIdLocationCount.hasOwnProperty(callId.toString())) {
                    if (
                        new Set(this.callIdLocationCount[callId.toString()]).size >
                        this.assignmentsConf.staff_overload
                    ) {
                        return true;
                    }
                }
            }
            return false;
        },

        /**
         * Gets assignment from assignments object according to given type and shift
         * @param {String} type Type of assignment: direct, or duty, or default
         * @param {int} shift Shift No (0 - 9) or undefined for direct
         * @param {object} assignment Assignment object from location
         * @return {object} Assignment object with (maybe) observers and assistance keys inside
         */
        assignmentChain(type, shift = undefined, assignment) {
            return type === 'direct'
                ? (assignment || {})[type] || {}
                : ((assignment || {})[type] || {})[shift] || {};
        },

        /**
         * Gets default assignment according to given call type and shift
         * @param {int} shift Shift No (0 - 9)
         * @param {String} call Call type - observers/assistance
         * @param {object} assignment Assignments object from location
         * @return {object[]} Default assignments array, up to 4 elements, each object with a single key as
         *                    message group name and value as array of phone numbers (strings)
         */
        defaultAssignmentChain(shift, call, assignment) {
            return ((((assignment || {}).default || {})[shift] || {})[call] || {}).chain || [];
        },

        /**
         * Gets default assignment hunting chain color according to given call type and shift
         * @param {int} shift Shift No (0 - 9)
         * @param {String} call Call type - observers/assistance
         * @param {object} assignment Assignments object from location
         * @return {int} Code of hunting chain color
         */
        defaultAssignmentColor(shift, call, assignment) {
            return ((((assignment || {}).default || {})[shift] || {})[call] || {}).color || 0;
        },

        /**
         * Sorts array of call ids
         * @return {array} sorted array of call ids
         */
        sortCallIds(arr) {
            let removed = arr.splice(0, 1);
            arr = arr.sort((a, b) => a - b);
            return removed.concat(arr);
        },

        /**
         * Gets last part of default assignment according to given call type and shift
         * @param {int} shift Shift No (0 - 9)
         * @param {String} call Call type - observers/assistance
         * @param {object} assignment Assignments object from location
         * @return {String[]} Last element from default assignments array, expanded to simple array, first element
         *                    is a message group name, following elements are phone numbers of that message group
         */
        defaultAssignmentChainLastPart(shift, call, assignment) {
            let asg = this.defaultAssignmentChain(shift, call, assignment);
            if (asg.length) {
                let last = asg[asg.length - 1];
                let huntingChain = [Object.keys(last)[0], ...last[Object.keys(last)[0]]];
                return this.sortCallIds(huntingChain);
            }

            return [];
        },

        /**
         * Get part of assignment specified by call type and number inside it
         * @param {String} call Call type - observers/assistance
         * @param {int} n Number of assignment inside escalation chain (first - 0 or second - 1)
         * @param {object} assignmentChain Assignment object with (maybe) observers and assistance keys inside
         * @return {{ users: int[], groups: int[] }} Assignment chain part object
         */
        assignmentChainPart(call, n, assignmentChain) {
            let chainPart = ((assignmentChain || {})[call] || [])[n] || {};
            return {
                users: chainPart.users || null,
                groups: chainPart.groups || null,
            };
        },

        /**
         * Creates complete chain with 2 elements
         * @param {String} call Call type - observers/assistance
         * @param {object} chain
         * @return {{users: int[], groups: int[]}[]}
         */
        assignmentChainComplete(call, chain) {
            return [this.assignmentChainPart(call, 0, chain), this.assignmentChainPart(call, 1, chain)];
        },

        /**
         * Calculates if full default chain is used as catch net (or only last part of the chain)
         * @param {int} shift - Shift No (0 - 9)
         * @param {String} call Call type - observers/assistance
         * @param {Object} assignments Assignments object
         * @return {Boolean}
         */
        defaultAssignmentOnly(shift, call, assignments) {
            let directFull = this.assignmentChainComplete(
                call,
                this.assignmentChain('direct', undefined, assignments)
            );
            let dutyFull = this.assignmentChainComplete(
                call,
                this.assignmentChain('duty', shift, assignments)
            );

            return (
                !(directFull[0].users || []).length &&
                !(directFull[0].groups || []).length &&
                !(directFull[1].users || []).length &&
                !(directFull[1].groups || []).length &&
                !(dutyFull[0].users || []).length &&
                !(dutyFull[0].groups || []).length &&
                !(dutyFull[1].users || []).length &&
                !(dutyFull[1].groups || []).length
            );
        },

        /**
         * Adds/removes staff from given assignments object
         * @param {string} type Type of assignment: direct or duty
         * @param {string} call Call type: observers or assistance
         * @param {int} shift Shift number (0 - 9)
         * @param {int} n Number of assignment inside escalation chain (first - 0 or second - 1)
         * @param {Object} staff Staff to be removed/included
         * @param {Boolean} operation true for staff addition and false for removal
         * @param {Object} assignments Assignments object
         * @param {number} location_id Current location id
         * @return {Object} Assignments object
         */
        changeSelectedStaff(type, call, shift, n, staff, operation, assignments, location_id) {
            let assignmentsCopy = JSON.parse(JSON.stringify(assignments));
            let chain = this.assignmentChainComplete(
                call,
                this.assignmentChain(type, shift, assignmentsCopy)
            );

            let staffType = staff.isGroup ? 'groups' : 'users';
            let idx = (chain[n][staffType] || []).indexOf(staff.id);
            if (idx === -1) {
                if (operation) {
                    if (Array.isArray(chain[n][staffType])) {
                        chain[n][staffType].push(staff.id);
                    } else {
                        chain[n][staffType] = [staff.id];
                    }
                    this.updateOverloadedStaff(staff, operation, location_id);
                }
            } else {
                if (!operation) {
                    chain[n][staffType].splice(idx, 1);
                    if (chain[n][staffType].length === 0) {
                        chain[n][staffType] = null;
                    }
                    this.updateOverloadedStaff(staff, operation, location_id);
                }
            }

            // create nested object structure if necessary and set result to location's assignments
            if (type === 'direct') {
                if (!assignmentsCopy.direct) {
                    assignmentsCopy.direct = { [call]: chain };
                } else {
                    assignmentsCopy.direct[call] = chain;
                }
            } else {
                if (!assignmentsCopy.duty) {
                    assignmentsCopy.duty = { [shift]: { [call]: chain } };
                } else if (!assignmentsCopy.duty[shift]) {
                    assignmentsCopy.duty[shift] = { [call]: chain };
                } else {
                    assignmentsCopy.duty[shift][call] = chain;
                }
            }

            return assignmentsCopy;
        },

        /**
         * Update list locations for call id when change assignments
         * @param {object} staff added or removed staff
         * @param {boolean} operation true for staff addition and false for removal
         * @param {number} locationId location id where add/remove staff
         */
        updateOverloadedStaff(staff, operation, locationId) {
            if (operation) {
                staff.callIds.forEach(callNumber => {
                    let callId = callNumber.toString();
                    if (this.callIdLocationCount.hasOwnProperty(callId)) {
                        this.callIdLocationCount[callId].push(locationId);
                    } else {
                        this.callIdLocationCount[callId] = [locationId];
                    }
                });
            }
            if (!operation) {
                staff.callIds.forEach(callIdNumber => {
                    let callId = callIdNumber.toString();
                    if (this.callIdLocationCount.hasOwnProperty(callId)) {
                        this.callIdLocationCount[callId].splice(
                            this.callIdLocationCount[callId].indexOf(locationId),
                            1
                        );
                    }
                });
            }
        },

        /**
         * Removes unnecessary locations from given list and sorts it for correct order to display
         * @param locations {object[]}
         * @return {object[]}
         */
        cleanLocations(locations) {
            if (!locations.length) {
                return [];
            }

            /**
             * Recursively rebuilds locations array
             * @param a {object[]} accumulator for location entries
             * @param loc {object} location object to start from
             * @return {object[]} array of location objects in correct order (DFS pre-order, wiki/Tree_Traversal)
             */
            let makeLocationsOrdered = (a, loc) => {
                let children = locations.filter(l => l.parent_id === loc.location_id);
                return children && children.length > 0
                    ? [...a, loc, ...children.reduce(makeLocationsOrdered, [])]
                    : [...a, loc];
            };

            let rootLocation = locations.find(l => l.type === 1);
            if (!rootLocation) {
                return [];
            }

            // locations are ordered from root to the last leaves of locations tree (correct order for output)
            let orderedLocations = makeLocationsOrdered([], rootLocation);

            let locationsToRemove = new Set();

            for (let i = orderedLocations.length - 1; i >= 0; i--) {
                // mark locations to remove
                if (![6, 7, 8].includes(orderedLocations[i].type)) {
                    locationsToRemove.add(orderedLocations[i].location_id);
                }
            }

            // correct parent_id-s for children of entries marked for removal
            // set parent_id to null if it's upper level

            for (let i = 0; i < orderedLocations.length; i++) {
                if (locationsToRemove.has(orderedLocations[i].location_id)) {
                    orderedLocations
                        .filter(l => l.parent_id === orderedLocations[i].location_id)
                        .forEach(l => {
                            l.parent_id = !i ? null : orderedLocations[i].parent_id;
                        });
                }
            }

            return orderedLocations.filter(l => !locationsToRemove.has(l.location_id));
        },

        /**
         * Generates common array with staff and staff groups and returns it
         * @param members {array}
         * @param groups {array}
         * @return {array}
         */
        makeStaffArray(members, groups) {
            let staffArray = [].concat(
                members.map(e => ({
                    title: e.first_name,
                    isGroup: false,
                    id: e.user_id,
                    assignable: e.assignable,
                    callIds: e.call_id ? [e.call_id] : [],
                    type: e.type,
                    overloaded: false,
                })),
                groups.map(e => {
                    let callIds = e.users
                        ? e.users
                              .filter(i => i.assignable && !!i.call_id)
                              .map(i => i.call_id)
                              .sort((a, b) => a - b)
                        : [];

                    return {
                        title: e.name,
                        isGroup: true,
                        id: e.staff_group_id,
                        assignable: e.assignable,
                        callIds,
                    };
                })
            );

            return orderBy(staffArray, 'title');
        },

        /**
         * Generates duties array with sorting by start time and night shift moved to the end and returns it
         * @param duties {object}
         * @return {array}
         */
        generateDutiesArray(duties) {
            return convertDuties(this.$t.bind(this), duties.duties, duties.has_selector);
        },

        /**
         * Prepares array of locations for saving - removes empty cells from it
         * @param {Array} locations
         * @return {Array}
         */
        prepareLocationsForSaving(locations) {
            /**
             * Implements 'map' function for object. Returns object with the same properties as the input one.
             * @param {Function} fn
             * @param {Object} obj
             * @return {*}
             */
            const mapObject = (fn, obj) =>
                typeof obj === 'object' && obj !== null
                    ? Object.keys(obj).reduce((a, key) => ({ ...a, [key]: fn(obj[key], key) }), {})
                    : obj;

            /**
             * Check if call is empty (users and groups are not present for every escalation)
             * @param {Array || null} cell
             * @return {Boolean}
             */
            const checkCallIsEmpty = cell =>
                !cell ||
                cell.every(esc => (!esc.users || !esc.users.length) && (!esc.groups || !esc.groups.length));

            /**
             * Processes all call types, observers/assistance right now
             * and replaces empty cells with null values
             * @param {Object | null} calls
             * @return {Object | null}
             */
            const mapCalls = calls => mapObject(call => (checkCallIsEmpty(call) ? undefined : call), calls);

            return locations.map(location => ({
                location_id: location.location_id,
                assign_id: location.assign_id,
                assignments: !location.assignments
                    ? undefined
                    : {
                          direct: mapCalls(location.assignments.direct),
                          duty: mapObject(mapCalls, location.assignments.duty),
                      },
            }));
        },

        /**
         * Deep diff between two object, using lodash
         * @param  {Object} object Object compared
         * @param  {Object} base   Object to compare with
         * @return {Object}        Return a new object which represent the diff
         */
        difference(object1, base1) {
            // Before comparison, discard unimportant fields
            let object = this.prepareLocationsForSaving([object1])[0];
            let base = this.prepareLocationsForSaving([base1])[0];

            const changes = (object, base) =>
                transform(object, (result, value, key) => {
                    if (!isEqual(value, base[key])) {
                        result[key] =
                            isObject(value) && isObject(base[key]) ? changes(value, base[key]) : value;
                    }
                });

            return changes(object, base);
        },
    },
};

/**
 * Converts data from "/api/v1/assignmentareas/ID/duties" format to the list of
 * options for SelectBox component. Sorts shifts by start time or by shift Nr
 * when hasSelector flag is set.
 *
 * @param {Function} translate
 * @param {Array | undefined | null} duties
 * @param {boolean} hasSelector flag if duty selector is present and controls current shift
 * @returns {Array}
 */
export function convertDuties(translate, duties, hasSelector) {
    let nightShift;
    let dutiesArr = [];
    const timeToSec = t => parseInt(t.slice(0, 2)) * 60 + parseInt(t.slice(3, 5));

    let dutiesWithStartTime = hasSelector
        ? (duties || []).sort((a, b) => {
              if (Number(a.state) === 0) {
                  return 1;
              }
              if (Number(b.state) === 0) {
                  return -1;
              }

              return a.state - b.state;
          })
        : (duties || [])
              .filter(item => item.start_time !== null)
              .sort((a, b) => (timeToSec(a.start_time) > timeToSec(b.start_time) ? 1 : -1));
    let clarification;
    let d;
    for (let i in dutiesWithStartTime) {
        if (hasSelector) {
            clarification = () => translate('assignments.controledBy');
        } else {
            let dutyStartTime = dutiesWithStartTime[i].start_time;
            let dutyEndTime = dutiesWithStartTime[parseInt(i) + 1]
                ? dutiesWithStartTime[parseInt(i) + 1].start_time
                : dutiesWithStartTime[0].start_time;
            clarification = () =>
                translate('assignments.isActiveBetween', {
                    val: formatTime(dutyStartTime.substr(0, 5)),
                    val1: formatTime(dutyEndTime.substr(0, 5)),
                });
        }
        d = {
            name: dutiesWithStartTime[i].duty_name,
            value: dutiesWithStartTime[i].state,
            clarification: clarification,
        };
        if (dutiesWithStartTime[i].state !== 0) {
            dutiesArr.push(d);
        } else if (dutiesWithStartTime[i].state === 0) {
            nightShift = d;
        }
    }
    if (nightShift) {
        dutiesArr.push(nightShift);
    }

    return dutiesArr;
}
